import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Lock, Search } from "lucide-react";
import { cn } from "@nexus/ui";
import type { AskNexusConversationSummary } from "@/features/ask-nexus/api/ask-nexus.api";
import { AskNexusWorking } from "@/features/ask-nexus/components/AskNexusWorking";
import { groupHistory, type HistoryGroupId } from "@/features/ask-nexus/lib/history-groups";
import { askNexusErrorMessage } from "@/features/ask-nexus/lib/error-message";
import { ASK_NEXUS_ICON_STROKE, askNexusColumnClass } from "@/features/ask-nexus/lib/ui";
import { useAskNexusPanel } from "@/features/ask-nexus/store/ask-nexus-panel.store";
import { askNexusConversationsQueryOptions } from "@/lib/queries/ask-nexus.queries";

const GROUP_LABELS: Record<HistoryGroupId, { key: string; fallback: string }> = {
  running: { key: "askNexus.history.groups.running", fallback: "Answering now" },
  today: { key: "askNexus.history.groups.today", fallback: "Today" },
  yesterday: { key: "askNexus.history.groups.yesterday", fallback: "Yesterday" },
  lastWeek: { key: "askNexus.history.groups.lastWeek", fallback: "Previous 7 days" },
  older: { key: "askNexus.history.groups.older", fallback: "Older" },
};

/** The caller's own chats in the active organization; the server returns nothing else. */
export function AskNexusHistory({
  tenantId,
  tenantName,
  activeConversationId,
  onOpen,
}: {
  tenantId: string;
  tenantName: string;
  activeConversationId: string | null;
  onOpen: (conversationId: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const isWide = useAskNexusPanel((s) => s.isWide);
  const [query, setQuery] = useState("");
  const { data, isPending, isError, error } = useQuery(askNexusConversationsQueryOptions(tenantId));
  const groups = useMemo(() => groupHistory(data ?? [], new Date(), query), [data, query]);

  return (
    <div className="relative min-h-0 flex-1 overflow-x-hidden overflow-y-auto" data-testid="ask-nexus-history">
      <div className={cn("mx-auto px-4 pt-4 pb-6", askNexusColumnClass(isWide))}>
        <label className="flex h-10 items-center gap-2 rounded-full border border-(--an-hair-strong) bg-(--an-field) px-3.5 text-(--an-ink-3)">
          <Search className="size-3.5 flex-none" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
          <input
            className="min-w-0 flex-1 border-0 bg-transparent text-sm text-(--an-ink) outline-none placeholder:text-(--an-ink-3)"
            aria-label={t("askNexus.history.search", "Search chats")}
            placeholder={t("askNexus.history.search", "Search chats")}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </label>
        {isPending ? (
          <AskNexusWorking className="mt-6 px-2" text={t("askNexus.history.loading", "Loading chats…")} />
        ) : isError ? (
          <p className="mt-6 px-2 text-[13px] text-(--an-neg-ink)" role="alert">
            {askNexusErrorMessage(t, error, tenantName)}
          </p>
        ) : groups.length === 0 ? (
          <p className="mt-6 px-2 text-[13px] text-(--an-ink-2)">
            {query.trim()
              ? t("askNexus.history.noMatches", "No chats match your search.")
              : t("askNexus.history.empty", "No chats yet in {{tenant}}.", { tenant: tenantName })}
          </p>
        ) : (
          groups.map((group) => (
            <section key={group.id}>
              <h3 className="mx-2 mt-6 mb-1.5 text-[10px] font-medium tracking-[0.09em] text-(--an-ink-2) uppercase">
                {t(GROUP_LABELS[group.id].key, GROUP_LABELS[group.id].fallback)}
              </h3>
              <ul>
                {group.items.map((item) => (
                  <li key={item.conversationId}>
                    <button
                      type="button"
                      className="block w-full rounded-xl px-3 py-2.5 text-left hover:bg-(--an-inset) aria-[current=true]:bg-(--an-inset)"
                      aria-current={item.conversationId === activeConversationId}
                      onClick={() => onOpen(item.conversationId)}
                    >
                      <span className="flex items-baseline gap-2">
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">{item.title}</span>
                        <span className="text-xs whitespace-nowrap text-(--an-ink-3)">{formatWhen(item, group.id, i18n.language)}</span>
                      </span>
                      {item.status === "running" ? (
                        <span className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-(--an-ink-2)">
                          <span className="ask-nexus-spin size-3 flex-none" aria-hidden="true" />
                          {t("askNexus.history.answering", "Answering…")}
                        </span>
                      ) : null}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
        <p className="mt-8 flex items-center justify-center gap-1.5 text-[11px] text-(--an-ink-3)">
          <Lock className="size-3 flex-none" strokeWidth={ASK_NEXUS_ICON_STROKE} aria-hidden="true" />
          {t("askNexus.history.scope", "Only {{tenant}} chats are listed.", { tenant: tenantName })}
        </p>
      </div>
    </div>
  );
}

/** A time for recent chats, a weekday within the week, a date before that. */
function formatWhen(item: AskNexusConversationSummary, group: HistoryGroupId, locale: string): string {
  const date = new Date(item.updatedAt);
  if (group === "lastWeek") return date.toLocaleDateString(locale, { weekday: "short" });
  if (group === "older") return date.toLocaleDateString(locale, { day: "numeric", month: "short" });
  return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}
